import classNames from 'classnames'
import { useFormik } from 'formik'
import React, { ChangeEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/button'
import { BackButton } from '../../components/button/backButton'
import { CategoryCard } from '../../components/cards/categoryCard'
import { AvailableHoursView } from '../../components/common/availableHoursView'
import { CreatorSectionSummary } from '../../components/common/creator-section-summary'
import { SettlementAccountDetails } from '../../components/common/settlementAccountDetails'
import { VendorAddress } from '../../components/common/vendorAddress'
import { VendorDeliveryRegions } from '../../components/common/vendorDeliveryRegions'
import { VendorFulfilmentCenter } from '../../components/common/vendorFulfilmentCenter'
import { ImageBanner } from '../../components/images/imageBanner'
import { Checkbox } from '../../components/inputs/Checkbox'
import { EditInput } from '../../components/inputs/editInput'
import { TextInput } from '../../components/inputs/textInput'
import { ImageUploadPreviewModal } from '../../components/modal/app_modals/ImagePreviewModal'
import { SelectCategoriesModal } from '../../components/modal/app_modals/selectCategoriesModal'
import { defaultAvailableHours } from '../../constants'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import { DeliveryRegion } from '../../redux-toolkits/locations/locations.type'
import { useAddCreatorMutation } from '../../redux-toolkits/vendors/vendors.slice'
import {
  AddCreatorPayload,
  AddCreatorTemplate,
  DeliveryTime,
} from '../../redux-toolkits/vendors/vendors.types'
import { FulfilmentCentre } from '../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { AddCreatorSchema } from '../../validation/validate'

type CreatorDeliveryLocation = {
  deliveryTime: string
  locationId: DeliveryRegion
  locationType: 'region'
}

const creatorFulfillmentTypeOptions = [
  {
    label: 'Onsite',
    description: 'Good warehouses and fulfils this creator’s products.',
    value: false,
  },
  {
    label: 'Offsite',
    description: 'The creator fulfils orders directly.',
    value: true,
  },
]

const businessInformation = [
  {
    key: 'businessName',
    title: 'Business name',
    value: '',
    placeholder: 'Enter business name',
    type: 'text',
  },
  {
    key: 'businessEmail',
    title: 'Business email address',
    value: '',
    placeholder: 'Enter business email address',
    type: 'text',
  },
  {
    key: 'tinNumber',
    title: 'Tax Identification Number',
    value: '',
    placeholder: 'Enter business TIN',
    type: 'text',
  },
  {
    key: 'cacNumber',
    title: 'CAC Number',
    value: '',
    placeholder: 'Enter business CAC number',
    type: 'text',
  },
  {
    key: 'businessStructure',
    title: 'Business structure',
    value: '',
    placeholder: 'Enter business structure e.g Sole enterpreneur',
    type: 'text',
  },
  {
    key: 'businessDescription',
    title: 'Business bio',
    value: '',
    placeholder: 'Enter business bio',
    type: 'textarea',
  },

  //   {
  //     key: 'extendedBio',
  //     title: 'Business short bio',
  //     value: '',
  //     placeholder: 'Enter business bio',
  //     type: 'textarea',
  //   },
  {
    key: 'businessPhonenumber',
    title: 'Business phone number',
    value: '',
    placeholder: 'Enter business phone number',
    type: 'text',
  },
  {
    key: 'website',
    title: 'Business website',
    value: '',
    placeholder: 'Enter business website',
    type: 'text',
  },
]

export const InviteVendorPage: React.FC = () => {
  const [selectedFulfilmetnCenters, setSelectedFulfilmetnCenters] =
    useState<FulfilmentCentre[]>()
  const [uploadedBanner, setUploadedBanner] = useState<File>()
  const [uploadedLogo, setUploadedLogo] = useState<File>()
  const [uploadMode, setUploadMode] = useState('')
  const [creatorDeliveryLocations, setCreatorDeliveryLocations] = useState<
    CreatorDeliveryLocation[]
  >([])
  const navigate = useNavigate()

  const { handleModalOpen, setModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl()

  const [addCreator, { isLoading: addingCreator }] = useAddCreatorMutation()

  const handleFileChange = (
    event: ChangeEvent<HTMLInputElement>,
    bannerOrIcon: 'logo' | 'banner' = 'banner',
  ) => {
    if (!event.target.files) {
      return
    }
    setUploadMode(bannerOrIcon)
    const selectedFile = event.target.files[0]
    bannerOrIcon === 'banner'
      ? setUploadedBanner(selectedFile)
      : setUploadedLogo(selectedFile)

    handleModalOpen('imageUploadPreviewModal')
  }

  const {
    values,
    handleChange,
    handleBlur,
    errors,
    setFieldValue,
    handleSubmit,
    touched,
  } = useFormik({
    initialValues: {
      logo: '',
      banner: '',
      website: '',
      businessPhonenumber: '',
      extendedBio: '',
      businessDescription: '',
      cacNumber: '',
      tinNumber: '',
      businessEmail: '',
      businessName: '',
      isOffsiteCreator: false,
      isSplitEnabled: false,
      splitPercentage: 0,
      businessAddress: undefined,
      location: undefined,
      servicesOffered: [],
      businessStructure: '',
      bankCode: '',
      accountNumber: '',
      availableHours: defaultAvailableHours,
      category: [],
      fulfilmentCentres: '',
      deliveryLocation: [],
    } as Partial<AddCreatorTemplate>,
    validationSchema: AddCreatorSchema,
    onSubmit: async (vals) => {
      const payload = Object.entries(vals).reduce(
        (
          acc: Record<string, string | boolean | object | number | undefined>,
          [key, value],
        ) => {
          if (!values.isSplitEnabled) acc['splitPercentage'] = undefined
          if (value !== '' && value !== undefined) acc[key] = value
          if (key === 'category') acc[key] = values.category?.map((c) => c._id)
          return acc
        },
        {} as Record<string, string>,
      ) as Partial<AddCreatorPayload>

      const resp = await addCreator({ ...payload })

      if (resp.data?.success) {
        navigate('/creators')
      }
    },
  })

  return (
    <Layout>
      <div className="flex flex-col gap-10">
        <div className="items-center flex justify-between gap-4">
          <BackButton
            onClick={() => {
              navigate('/creators')
            }}
          />

          <Button
            label="Add Creator"
            onClick={() => {
              handleSubmit()
            }}
            primary
            className=""
            loading={addingCreator}
            disabled={addingCreator}
          />
        </div>

        <div className="flex flex-col gap-2 text-sec-black">
          {/* Banner and logo  */}
          <div>
            <ImageBanner
              imageUrl={values.banner ?? ''}
              loading={false}
              className="!max-h-[280px]"
              errors={errors.banner ?? errors.logo}
              onImageEdit={(e) => {
                handleFileChange(e, 'banner')
              }}
              editIconPos="top-right"
            />

            <ImageBanner
              imageUrl={values?.logo ?? ''}
              loading={false}
              className="-mt-10 z-10 relative ml-4 !rounded-full !max-w-[80px] !max-h-[80px]"
              onImageEdit={(e) => {
                handleFileChange(e, 'logo')
              }}
              editIconPos="bottom-right"
              initials={`${values?.businessName?.charAt(0)}`}
            />
          </div>
        </div>

        <Checkbox
          checkStatus={!!values.isSplitEnabled}
          handleChange={(status) => {
            setFieldValue('isSplitEnabled', status)
          }}
          text="Enable split for this creator?"
        />

        {values.isSplitEnabled && (
          <div
            className="
              flex flex-col gap-4
              first:pt-0
              "
          >
            <TextInput
              name={'splitPercentage'}
              hasIcon={false}
              value={String(values.splitPercentage ?? '')}
              onChange={handleChange}
              onBlur={handleBlur}
              type={'text'}
              placeholder={'Enter split percentage'}
              errors={
                errors.splitPercentage && touched.splitPercentage
                  ? errors.splitPercentage
                  : ''
              }
              title="Split Percentage"
            />
          </div>
        )}

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 pb-4 border-b">
            {businessInformation.map((info, idx) => {
              const key = `${info.key}-${idx}`
              const inputKey = info.key as keyof typeof values

              return (
                <TextInput
                  name={info.key}
                  hasIcon={false}
                  value={values[inputKey] as string}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type={info.type}
                  placeholder={info.placeholder}
                  errors={
                    errors[inputKey] && touched[inputKey]
                      ? (errors[inputKey] as string)
                      : ''
                  }
                  title={info.title}
                  key={key}
                  containerClassName={classNames('max-w-[620px]', {
                    'col-span-full': info.key === 'businessDescription',
                  })}
                />
              )
            })}
          </div>

          <EditInput
            title="Business Category"
            onEditSave={() => {}}
            editing={false}
            onEditClick={() => {
              handleModalOpen('manageCategoriesModal')
            }}
            onCancel={() => {}}
            loading={false}
            className="pb-4 border-b"
          >
            {() => {
              return (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-6 flex-wrap">
                    {values.category?.map((cat, idx) => {
                      return (
                        <CategoryCard
                          category={cat}
                          key={`${cat?._id}-${idx}`}
                        />
                      )
                    })}
                  </div>
                  <CreatorSectionSummary categories={values.category} />
                </div>
              )
            }}
          </EditInput>

          <AvailableHoursView
            availableHours={values.availableHours}
            className="pb-4 border-b"
            loading={false}
            onModify={async (availHours) => {
              setFieldValue('availableHours', availHours)
            }}
          />

          <SettlementAccountDetails
            vendor={undefined}
            className="pb-4 border-b"
            loading={false}
            onModify={async (settlementAccount) => {
              setFieldValue('bankCode', settlementAccount.bankCode)
              setFieldValue('accountNumber', settlementAccount.accountNumber)
            }}
            error={errors.accountNumber ? 'Settlement account is required' : ''}
          />

          <VendorAddress
            address={values.businessAddress?.address}
            className="pb-4 border-b"
            loading={false}
            onModify={async ({ data: { placeId, ...data }, close }) => {
              setFieldValue('businessAddress', data)
              // setFieldValue('location', {
              //   coordinates: [data.longitude, data.latitude],
              //   sourceGooglePlaceID: placeId,
              // })
              close()
            }}
            error={errors.businessAddress}
          />

          <VendorFulfilmentCenter
            fulfilmentCenters={selectedFulfilmetnCenters}
            className="pb-4 border-b"
            loading={false}
            onModify={async (data, handleClose) => {
              setFieldValue(
                'fulfilmentCentres',
                data.fulfilmentCenters.map((f) => f._id),
              )
              setSelectedFulfilmetnCenters(data.fulfilmentCenters)
              handleClose()
            }}
          />

          <div className="pb-4 border-b">
            <p className="mb-3 text-sm font-medium text-black">Creator type</p>

            <div
              className="grid w-full grid-cols-1 gap-3 md:grid-cols-2"
              role="radiogroup"
              aria-label="Creator type"
            >
              {creatorFulfillmentTypeOptions.map((option) => {
                const active = option.value === !!values.isOffsiteCreator

                return (
                  <button
                    key={option.label}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={classNames(
                      'flex min-h-[92px] flex-col items-start justify-center rounded-[8px] border px-4 py-3 text-left transition-all',
                      active
                        ? 'border-black bg-black text-white'
                        : 'border-divider-gray bg-white text-black hover:border-black',
                    )}
                    onClick={() => {
                      setFieldValue('isOffsiteCreator', option.value)
                    }}
                  >
                    <span className="text-sm font-medium">{option.label}</span>
                    <span
                      className={classNames('mt-1 text-xs leading-5', {
                        'text-light-gray': active,
                        'text-sec-black': !active,
                      })}
                    >
                      {option.description}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <VendorDeliveryRegions
            vendor={
              {
                deliveryLocation: creatorDeliveryLocations,
              } as any
            }
            loading={false}
            onSuccess={(data, handleClose) => {
              const nextLocations = data.regions.map((region) => ({
                deliveryTime: region.deliveryTime as DeliveryTime,
                locationId: {
                  _id: region.regionId,
                  name: region.regionName,
                } as DeliveryRegion,
                locationType: 'region' as const,
              }))

              setCreatorDeliveryLocations(nextLocations)
              setFieldValue(
                'deliveryLocation',
                data.regions.map((region) => ({
                  locationId: region.regionId,
                  deliveryTime: region.deliveryTime,
                  locationType: 'region',
                })),
              )
              handleClose()
            }}
          />
        </div>
      </div>

      {uploadedBanner || uploadedLogo ? (
        <ImageUploadPreviewModal
          successToast={false}
          image={uploadedBanner || (uploadedLogo as File)}
          open={modal === 'imageUploadPreviewModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            uploadedBanner
              ? setUploadedBanner(undefined)
              : setUploadedLogo(undefined)
          }}
          uploadMode={uploadMode}
          onSuccessCallback={(url) => {
            if (uploadedBanner) {
              setFieldValue('banner', url)
            } else {
              setFieldValue('logo', url)
            }
          }}
        />
      ) : null}

      <SelectCategoriesModal
        open={modalOpen && modal === 'manageCategoriesModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={(categories) => {
          setFieldValue('category', categories)
          handleModalClose()
        }}
        selectedCategories={values.category}
        loading={false}
      />
    </Layout>
  )
}
